import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Button,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import {
    Search as SearchIcon,
    GetApp as DownloadIcon,
    FilterList as FilterIcon,
    Sort as SortIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { CustomPagination } from '../../../Tables/Pagination';
import { IPagination } from '../../../../hook/usePagination';
import _ from 'lodash';
import { IManagedEventSales, IManagedEvenetResponseData } from '../../../../redux/events/types';
import { CustomDatePicker } from '../../../DatePicker/CustomDatePicker';
import { ColumnsData, Table } from '../../../Tables/Table';

interface EventSalesTabProps {
    sales?: IManagedEventSales;
    pagination: IPagination;
    sortOrder: 'asc' | 'desc';
    onSearchChange: (searchText: string) => void;
    onStartDateChange: (date: string) => void;
    onEndDateChange: (date: string) => void;
    onSortOrderChange: (order: 'asc' | 'desc') => void;
    onRowsChange: (rows: number) => void;
    onPageChange: (page: number) => void;
    onClearSearch: () => void;
    onClearStartDate?: () => void;
    onClearEndDate?: () => void;
    onExportCSV: (sales?: IManagedEventSales) => void;
    loading?: boolean;
}

// Sort options
const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'amount_high', label: 'Amount (High to Low)' },
    { value: 'amount_low', label: 'Amount (Low to High)' },
];

export const EventSalesTab: React.FC<EventSalesTabProps> = ({
    sales,
    pagination,
    sortOrder,
    onSearchChange,
    onStartDateChange,
    onEndDateChange,
    onSortOrderChange,
    onRowsChange,
    onPageChange,
    onClearSearch,
    onClearStartDate,
    onClearEndDate,
    onExportCSV,
    loading = false,
}) => {
    const { themeStyle } = useThemeContext();
    const [searchValue, setSearchValue] = useState('');
    const [sortOption, setSortOption] = useState('newest');
    const [startDate, setStartDate] = useState<string | undefined>();
    const [endDate, setEndDate] = useState<string | undefined>();
    const [showFilters, setShowFilters] = useState(false);

    // Create debounced search handler
    const debouncedSearchHandler = useRef(
        _.debounce((searchText: string) => {
            if (onSearchChange) {
                onSearchChange(searchText);
            }
        }, 500),
    ).current;

    // Clean up debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSearchHandler.cancel();
        };
    }, [debouncedSearchHandler]);

    // Handle search input change
    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setSearchValue(newValue);
        debouncedSearchHandler(newValue);
    };

    // Handle clear search
    const handleClearSearch = () => {
        setSearchValue('');
        onClearSearch();
    };

    // Handle sort change
    const handleSortChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setSortOption(value);

        if (value === 'newest') {
            onSortOrderChange('desc');
        } else if (value === 'oldest') {
            onSortOrderChange('asc');
        } else if (value === 'amount_high') {
            onSortOrderChange('desc');
        } else if (value === 'amount_low') {
            onSortOrderChange('asc');
        }
    };

    // Handle date changes with clear functionality
    const handleStartDateChange = (date: string | undefined) => {
        setStartDate(date);
        if (date) {
            onStartDateChange(date);
        } else if (onClearStartDate) {
            onClearStartDate();
        }
    };

    const handleEndDateChange = (date: string | undefined) => {
        setEndDate(date);
        if (date) {
            onEndDateChange(date);
        } else if (onClearEndDate) {
            onClearEndDate();
        }
    };

    const handleClearDateFilters = () => {
        setStartDate(undefined);
        setEndDate(undefined);

        if (onClearStartDate) {
            onClearStartDate();
        } else {
            onStartDateChange('');
        }

        if (onClearEndDate) {
            onClearEndDate();
        } else {
            onEndDateChange('');
        }
    };

    // Table columns
    const columnsData: ColumnsData[] = [
        {
            field: 'name_used',
            label: 'Name',
            type: 'text',
            sortable: true,
            width: 160,
        },
        {
            field: 'email_used',
            label: 'Email',
            type: 'text',
            sortable: true,
            width: 180,
        },
        {
            field: 'items',
            label: 'Tickets',
            type: 'custom',
            sortable: false,
            customCell: (params: IManagedEvenetResponseData) => <Typography>{params.items.length}</Typography>,
            width: 100,
        },
        {
            field: 'formatted_price',
            label: 'Amount',
            type: 'text',
            sortable: true,
            width: 120,
        },
        {
            field: 'createdAt',
            label: 'Purchase Date',
            type: 'custom',
            sortable: true,
            customCell: (params: IManagedEvenetResponseData) => (
                <Typography>
                    {new Date(params.createdAt).toLocaleDateString()}{' '}
                    {new Date(params.createdAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                    })}
                </Typography>
            ),
            width: 180,
        },
        {
            field: 'status',
            label: 'Status',
            type: 'custom',
            sortable: false,
            customCell: (params: IManagedEvenetResponseData) => {
                return (
                    <Box
                        sx={{
                            backgroundColor: params.completed
                                ? theme[themeStyle].primaryGreen
                                : params.refunded
                                ? theme[themeStyle].primary_red
                                : theme[themeStyle].peachYellow,
                            borderRadius: '12px',
                            px: 2,
                            py: 0.5,
                            display: 'inline-block',
                        }}>
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color:
                                    !params.completed && !params.refunded
                                        ? theme[themeStyle].black
                                        : theme[themeStyle].primary_white,
                                textTransform: 'capitalize',
                            }}>
                            {params.completed ? 'Completed' : params.refunded ? 'Refunded' : 'Pending'}
                        </Typography>
                    </Box>
                );
            },
            width: 120,
        },
    ];

    return (
        <Box>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '1.25rem',
                    color: theme[themeStyle].primary_white,
                    mb: 3,
                }}>
                Sales
            </Typography>

            {/* Sales Summary */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Box
                        sx={{
                            backgroundColor: theme[themeStyle].table_header,
                            borderRadius: 2,
                            padding: 2,
                            height: '100%',
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: '0.875rem',
                                color: theme[themeStyle].light_gray_text,
                            }}>
                            Total Sales
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: '1.75rem',
                                color: theme[themeStyle].primary_white,
                                mt: 0.5,
                            }}>
                            {sales?.total?.formatted_amount || '$0.00'}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Box
                        sx={{
                            backgroundColor: theme[themeStyle].table_header,
                            borderRadius: 2,
                            padding: 2,
                            height: '100%',
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: '0.875rem',
                                color: theme[themeStyle].light_gray_text,
                            }}>
                            Tickets Sold
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: '1.75rem',
                                color: theme[themeStyle].primary_white,
                                mt: 0.5,
                            }}>
                            {sales?.total?.count || 0}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box
                        sx={{
                            backgroundColor: theme[themeStyle].table_header,
                            borderRadius: 2,
                            padding: 2,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}>
                        <Button
                            startIcon={<DownloadIcon />}
                            onClick={() => onExportCSV(sales)}
                            sx={{
                                color: theme[themeStyle].black,
                                backgroundColor: theme[themeStyle].highlight,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    opacity: 0.9,
                                },
                                py: 1.5,
                            }}>
                            Export Sales as CSV
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            {/* Search and Filter Controls */}
            <Box sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            placeholder="Search by name, ticket ID or email"
                            value={searchValue}
                            onChange={handleSearchInputChange}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme[themeStyle].tag_white }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchValue ? (
                                    <InputAdornment position="end">
                                        <CloseIcon
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                cursor: 'pointer',
                                            }}
                                            onClick={handleClearSearch}
                                        />
                                    </InputAdornment>
                                ) : null,
                                sx: {
                                    backgroundColor: theme[themeStyle].table_header,
                                    borderRadius: 2,
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].placeholderGray50,
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                startIcon={<FilterIcon />}
                                onClick={() => setShowFilters(!showFilters)}
                                sx={{
                                    color: theme[themeStyle].primary_white,
                                    borderColor: theme[themeStyle].placeholderGray,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    border: `1px solid ${theme[themeStyle].placeholderGray}`,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    backgroundColor: showFilters ? theme[themeStyle].primary_orange : 'transparent',
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        opacity: 0.9,
                                    },
                                }}>
                                Filters
                            </Button>

                            <FormControl
                                sx={{
                                    minWidth: 150,
                                    '& .MuiInputBase-root': {
                                        backgroundColor: theme[themeStyle].table_header,
                                        color: theme[themeStyle].primary_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        borderRadius: 2,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].placeholderGray50,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                }}>
                                <InputLabel id="sort-select-label" sx={{ color: theme[themeStyle].gray_text }}>
                                    Sort By
                                </InputLabel>
                                <Select
                                    labelId="sort-select-label"
                                    value={sortOption}
                                    onChange={handleSortChange}
                                    label="Sort By"
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <SortIcon sx={{ color: theme[themeStyle].tag_white }} />
                                        </InputAdornment>
                                    }>
                                    {SORT_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Date Range Filters */}
            {showFilters && (
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: theme[themeStyle].table_header,
                    }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={5}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.875rem',
                                    mb: 1,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                Date Range
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <CustomDatePicker label="From" value={startDate} onChange={handleStartDateChange} />
                                <Typography sx={{ color: theme[themeStyle].primary_white }}>-</Typography>
                                <CustomDatePicker label="To" value={endDate} onChange={handleEndDateChange} />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={7}>
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                }}>
                                <Button
                                    onClick={handleClearDateFilters}
                                    sx={{
                                        color: theme[themeStyle].primary_white,
                                        borderColor: theme[themeStyle].placeholderGray,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        border: `1px solid ${theme[themeStyle].placeholderGray}`,
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            opacity: 0.8,
                                        },
                                    }}>
                                    Clear Filters
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Table */}
            <Table
                columns={columnsData}
                staticData={sales?.data || []}
                rowsPerPageOptions={[5, 10, 20, 50]}
                tableType="sales"
                rowHeight={60}
                headerHeight={50}
                textSx={{ fontSize: 13.5 }}
                dashboardCategory="sales"
            />

            {/* Pagination */}
            {sales?.pagination && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <CustomPagination
                        pagination={{
                            page: pagination.page,
                            rows: pagination.rows,
                            totalCount: sales.pagination.totalCount,
                        }}
                        onPageChange={onPageChange}
                        onRowsChange={onRowsChange}
                        rowsPerPageOptions={[5, 10, 20, 50]}
                    />
                </Box>
            )}
        </Box>
    );
};
